import type { SupportedLocale } from '@erp/shared';

const en = {
  spreadsheetOriginals:
    'Original files from orgaMAX are preserved separately. Current master data was imported from a spreadsheet; no separate original master snapshot is available here.',
  fieldAvailability: 'Field availability',
  nativeFieldConfigurationChanged:
    'The field configuration changed while saving. Reload the record and try again.',
  nativeFieldUnpublished: 'Available on imported records; not yet generally published.',
  nativeFieldUnresolved:
    'Original value without a matching choice. Select a valid option or clear the value.',
  nativeFieldEvidenceInvalid:
    'The source of this field could not be verified. Please contact an administrator.',
  sourceReference: 'Source reference',
  showProvenance: 'Data origin',
  sourceField: 'Source field',
  sourceDefinition: 'Source field definition',
  sourceImageProcessingProtected:
    'Image variants of the imported original remain preserved as evidence. Use a newly uploaded image if needed.',
  sourceImageReplaceProtected:
    'The original migration image remains preserved as evidence. Upload a new image and, if needed, remove the old one from the current image collection.',
  bulkDecisionRequired:
    'A generic bulk import cannot overwrite this migrated current record without a reviewed field decision. Edit it in its normal form or use the verified migration replay.',
  receivablesDeferred: 'Opening receivables await an approved cutoff and source reconciliation.',
  sourcePreserved: 'Original source data remains unchanged.',
  editableExplanation:
    'Current master data can be edited with the usual permissions. Missing facts are checked before the operations that need them.',
  incompleteCurrentFacts: 'Some operations still require additional current facts.',
  priceRequired: 'Enter a price explicitly or select a valid price condition.',
  productInactive:
    'This product is inactive or archived and cannot be selected for a new transaction.',
  discountRestricted:
    'The original source prohibits discounts. Resolve the scope of this restriction before using a discount.',
  conversionRequired:
    'This unit conversion has not been approved. Use the original unit until it is reviewed.',
  inventoryDeferred: 'Stock postings await the approved opening inventory and cutoff date.',
  requiredFor: {
    EDIT: 'Complete the indicated facts before this operation.',
    DRAFT: 'Complete the indicated facts before this operation.',
    TAX: 'Complete the indicated facts before this operation.',
    PRICE: 'Complete the indicated facts before this operation.',
    STOCK: 'Complete the indicated facts before this operation.',
    BILLING: 'Complete the indicated facts before this operation.',
    SEND: 'Complete the indicated facts before this operation.',
    PORTAL: 'Complete the indicated facts before this operation.',
  },
  title: 'Data origin',
  masterReviewRequired: 'Imported data requires operational review.',
  holdExplanation:
    'New transactions, shipments and stock postings are blocked. Original values remain available; missing facts have not been replaced with defaults. Stock balances await an approved opening inventory.',
  origin: 'Source · {{system}}',
  originalFields: 'Original additional fields',
  sourceFacts: 'Preserved source values',
  loading: 'Loading source evidence',
  error: 'Source evidence could not be loaded.',
  noEvidence: 'The import has not yet published its verified source evidence.',
  noFields: 'No populated additional fields in this source record.',
};
export const migrationTranslations: Record<SupportedLocale, typeof en> = {
  en,
  de: {
    spreadsheetOriginals:
      'Originaldateien aus orgaMAX bleiben separat erhalten. Die aktuellen Stammdaten wurden aus einer Tabelle importiert; ein eigener Original-Stammdatensnapshot liegt hier nicht vor.',
    fieldAvailability: 'Nutzung dieser Felder',
    nativeFieldConfigurationChanged:
      'Die Feldkonfiguration wurde während des Speicherns geändert. Datensatz neu laden und erneut versuchen.',
    nativeFieldUnpublished:
      'An übernommenen Datensätzen verfügbar; noch nicht allgemein veröffentlicht.',
    nativeFieldUnresolved:
      'Ursprünglicher Wert ohne passende Auswahl. Gültige Option wählen oder den Wert leeren.',
    nativeFieldEvidenceInvalid:
      'Die Herkunft dieses Zusatzfelds konnte nicht geprüft werden. Bitte wenden Sie sich an die Administration.',
    sourceReference: 'Quellverweis',
    showProvenance: 'Datenherkunft',
    sourceField: 'Quellfeld',
    sourceDefinition: 'Felddefinition in der Quelle',
    sourceImageProcessingProtected:
      'Die Bildvarianten des importierten Originals bleiben als Nachweis erhalten. Verwenden Sie bei Bedarf ein neu hochgeladenes Bild.',
    sourceImageReplaceProtected:
      'Das ursprüngliche Migrationsbild bleibt als Nachweis erhalten. Laden Sie ein neues Bild hoch und entfernen Sie das alte bei Bedarf aus der aktuellen Bildsammlung.',
    bulkDecisionRequired:
      'Ein allgemeiner Massenimport darf diesen übernommenen Datensatz ohne geprüfte Feldentscheidung nicht überschreiben. Regulär im Formular bearbeiten oder den geprüften Migrationslauf wiederholen.',
    receivablesDeferred:
      'Offene Übernahmeposten warten auf den freigegebenen Stichtag und den Quellenabgleich.',
    sourcePreserved: 'Originale Quelldaten bleiben unverändert erhalten.',
    editableExplanation:
      'Aktuelle Stammdaten können mit den üblichen Berechtigungen bearbeitet werden. Fehlende Angaben werden vor den jeweils betroffenen Vorgängen geprüft.',
    incompleteCurrentFacts: 'Für einzelne Vorgänge fehlen noch aktuelle Angaben.',
    priceRequired: 'Preis ausdrücklich eingeben oder eine gültige Preiskondition auswählen.',
    productInactive:
      'Dieser Artikel ist inaktiv oder archiviert und kann nicht für einen neuen Vorgang ausgewählt werden.',
    discountRestricted:
      'Die Quelle enthält eine Rabattsperre. Ihr Geltungsbereich muss vor einer rabattierten Verwendung geklärt werden.',
    conversionRequired:
      'Diese Gebindeumrechnung ist noch nicht freigegeben. Bis zur Prüfung die Originaleinheit verwenden.',
    inventoryDeferred:
      'Lagerbuchungen warten auf die freigegebene Stichtagsinventur und den Übernahmestichtag.',
    requiredFor: {
      EDIT: 'Die genannten Angaben müssen vor diesem Vorgang vervollständigt werden.',
      DRAFT: 'Die genannten Angaben müssen vor diesem Vorgang vervollständigt werden.',
      TAX: 'Die genannten Angaben müssen vor diesem Vorgang vervollständigt werden.',
      PRICE: 'Die genannten Angaben müssen vor diesem Vorgang vervollständigt werden.',
      STOCK: 'Die genannten Angaben müssen vor diesem Vorgang vervollständigt werden.',
      BILLING: 'Die genannten Angaben müssen vor diesem Vorgang vervollständigt werden.',
      SEND: 'Die genannten Angaben müssen vor diesem Vorgang vervollständigt werden.',
      PORTAL: 'Die genannten Angaben müssen vor diesem Vorgang vervollständigt werden.',
    },
    title: 'Datenherkunft',
    masterReviewRequired: 'Übernommene Daten benötigen eine fachliche Freigabe.',
    holdExplanation:
      'Neue Vorgänge, Versand und Lagerbuchungen sind gesperrt. Originalwerte bleiben einsehbar; fehlende Angaben wurden nicht durch Standardwerte ersetzt. Lagerbestände warten auf eine freigegebene Stichtagsinventur.',
    origin: 'Herkunft · {{system}}',
    originalFields: 'Ursprüngliche Zusatzfelder',
    sourceFacts: 'Erhaltene Quellwerte',
    loading: 'Quellnachweis wird geladen',
    error: 'Der Quellnachweis konnte nicht geladen werden.',
    noEvidence: 'Der Import hat seinen geprüften Quellnachweis noch nicht veröffentlicht.',
    noFields: 'Dieser Quelldatensatz enthält keine ausgefüllten Zusatzfelder.',
  },
  fr: {
    spreadsheetOriginals:
      'Les fichiers originaux d’orgaMAX sont conservés séparément. Les données de base actuelles proviennent d’un tableur ; aucun instantané original distinct de ces données n’est disponible ici.',
    fieldAvailability: 'Utilisation de ces champs',
    nativeFieldConfigurationChanged:
      'La configuration du champ a changé pendant l’enregistrement. Rechargez la fiche puis réessayez.',
    nativeFieldUnpublished:
      'Disponible sur les fiches importées ; pas encore publié pour tous les usages.',
    nativeFieldUnresolved:
      'Valeur d’origine sans choix correspondant. Sélectionnez une option valide ou effacez la valeur.',
    nativeFieldEvidenceInvalid:
      'L’origine de ce champ n’a pas pu être vérifiée. Contactez l’administration.',
    sourceReference: 'Référence source',
    showProvenance: 'Origine des données',
    sourceField: 'Champ source',
    sourceDefinition: 'Définition du champ source',
    sourceImageProcessingProtected:
      'Les variantes de l’image originale importée restent conservées comme justificatifs. Utilisez une nouvelle image si nécessaire.',
    sourceImageReplaceProtected:
      'L’image originale de la migration reste conservée comme justificatif. Ajoutez une nouvelle image et retirez si nécessaire l’ancienne de la collection actuelle.',
    bulkDecisionRequired:
      'Un import générique ne peut pas écraser cet enregistrement repris sans décision validée par champ. Modifiez-le dans son formulaire habituel ou relancez la migration vérifiée.',
    receivablesDeferred:
      'Les soldes clients initiaux attendent une date de reprise approuvée et le rapprochement des sources.',
    sourcePreserved: 'Les données source originales restent inchangées.',
    editableExplanation:
      'Les données de base actuelles sont modifiables avec les autorisations habituelles. Les informations manquantes sont vérifiées avant les opérations qui en dépendent.',
    incompleteCurrentFacts:
      'Certaines opérations nécessitent encore des informations actuelles supplémentaires.',
    priceRequired: 'Saisissez explicitement un prix ou choisissez une condition tarifaire valide.',
    productInactive:
      'Cet article est inactif ou archivé et ne peut pas être sélectionné pour une nouvelle opération.',
    discountRestricted:
      'La source interdit les remises. Clarifiez la portée de cette restriction avant d’appliquer une remise.',
    conversionRequired:
      'Cette conversion de conditionnement n’est pas encore approuvée. Utilisez l’unité originale jusqu’à sa validation.',
    inventoryDeferred:
      'Les mouvements de stock attendent l’inventaire initial approuvé et sa date de reprise.',
    requiredFor: {
      EDIT: 'Complétez les informations indiquées avant cette opération.',
      DRAFT: 'Complétez les informations indiquées avant cette opération.',
      TAX: 'Complétez les informations indiquées avant cette opération.',
      PRICE: 'Complétez les informations indiquées avant cette opération.',
      STOCK: 'Complétez les informations indiquées avant cette opération.',
      BILLING: 'Complétez les informations indiquées avant cette opération.',
      SEND: 'Complétez les informations indiquées avant cette opération.',
      PORTAL: 'Complétez les informations indiquées avant cette opération.',
    },
    title: 'Origine des données',
    masterReviewRequired: 'Les données importées nécessitent une validation métier.',
    holdExplanation:
      'Les nouvelles opérations, les envois et les mouvements de stock sont bloqués. Les valeurs originales restent consultables ; les informations manquantes ne sont pas remplacées par des valeurs par défaut. Les stocks attendent un inventaire initial approuvé.',
    origin: 'Origine · {{system}}',
    originalFields: 'Champs supplémentaires d’origine',
    sourceFacts: 'Valeurs source conservées',
    loading: 'Chargement des justificatifs source',
    error: 'Les justificatifs source n’ont pas pu être chargés.',
    noEvidence: 'L’import n’a pas encore publié ses justificatifs source vérifiés.',
    noFields: 'Aucun champ supplémentaire renseigné dans cet enregistrement source.',
  },
  es: {
    spreadsheetOriginals:
      'Los archivos originales de orgaMAX se conservan por separado. Los datos maestros actuales se importaron de una hoja de cálculo; aquí no hay una instantánea original independiente de esos datos.',
    fieldAvailability: 'Uso de estos campos',
    nativeFieldConfigurationChanged:
      'La configuración del campo cambió durante el guardado. Vuelva a cargar el registro e inténtelo de nuevo.',
    nativeFieldUnpublished:
      'Disponible en registros importados; aún no publicado para uso general.',
    nativeFieldUnresolved:
      'Valor original sin opción coincidente. Seleccione una opción válida o borre el valor.',
    nativeFieldEvidenceInvalid:
      'No se pudo verificar el origen de este campo. Contacte con la administración.',
    sourceReference: 'Referencia de origen',
    showProvenance: 'Origen de los datos',
    sourceField: 'Campo de origen',
    sourceDefinition: 'Definición del campo de origen',
    sourceImageProcessingProtected:
      'Las variantes de la imagen original importada se conservan como evidencia. Use una imagen nueva si es necesario.',
    sourceImageReplaceProtected:
      'La imagen original de la migración se conserva como evidencia. Suba una nueva imagen y, si es necesario, retire la anterior de la colección actual.',
    bulkDecisionRequired:
      'Una importación masiva genérica no puede sobrescribir este registro migrado sin una decisión revisada por campo. Edítelo en su formulario habitual o repita la migración verificada.',
    receivablesDeferred:
      'Los saldos pendientes iniciales esperan una fecha de corte aprobada y la conciliación de las fuentes.',
    sourcePreserved: 'Los datos de origen originales permanecen sin cambios.',
    editableExplanation:
      'Los datos maestros actuales pueden editarse con los permisos habituales. Los datos ausentes se comprueban antes de las operaciones que los necesitan.',
    incompleteCurrentFacts: 'Algunas operaciones todavía requieren datos actuales adicionales.',
    priceRequired: 'Introduzca un precio explícito o seleccione una condición de precio válida.',
    productInactive:
      'Este artículo está inactivo o archivado y no puede seleccionarse para una operación nueva.',
    discountRestricted:
      'El origen prohíbe descuentos. Aclare el alcance de esta restricción antes de aplicar un descuento.',
    conversionRequired:
      'Esta conversión de envases aún no está aprobada. Use la unidad original hasta su revisión.',
    inventoryDeferred:
      'Los movimientos de existencias esperan el inventario inicial aprobado y la fecha de corte.',
    requiredFor: {
      EDIT: 'Complete los datos indicados antes de esta operación.',
      DRAFT: 'Complete los datos indicados antes de esta operación.',
      TAX: 'Complete los datos indicados antes de esta operación.',
      PRICE: 'Complete los datos indicados antes de esta operación.',
      STOCK: 'Complete los datos indicados antes de esta operación.',
      BILLING: 'Complete los datos indicados antes de esta operación.',
      SEND: 'Complete los datos indicados antes de esta operación.',
      PORTAL: 'Complete los datos indicados antes de esta operación.',
    },
    title: 'Origen de los datos',
    masterReviewRequired: 'Los datos importados requieren una validación operativa.',
    holdExplanation:
      'Las nuevas operaciones, los envíos y los movimientos de existencias están bloqueados. Los valores originales siguen disponibles; los datos ausentes no se sustituyen por valores predeterminados. Las existencias esperan un inventario inicial aprobado.',
    origin: 'Origen · {{system}}',
    originalFields: 'Campos adicionales originales',
    sourceFacts: 'Valores de origen conservados',
    loading: 'Cargando justificantes de origen',
    error: 'No se pudieron cargar los justificantes de origen.',
    noEvidence: 'La importación aún no ha publicado sus justificantes de origen verificados.',
    noFields: 'Este registro de origen no contiene campos adicionales cumplimentados.',
  },
  tr: {
    spreadsheetOriginals:
      'orgaMAX özgün dosyaları ayrı olarak korunur. Güncel ana veriler bir tablodan içe aktarılmıştır; burada ayrı bir özgün ana veri anlık görüntüsü yoktur.',
    fieldAvailability: 'Bu alanların kullanımı',
    nativeFieldConfigurationChanged:
      'Kaydetme sırasında alan yapılandırması değişti. Kaydı yeniden yükleyip tekrar deneyin.',
    nativeFieldUnpublished:
      'İçe aktarılan kayıtlarda kullanılabilir; henüz genel kullanıma yayımlanmadı.',
    nativeFieldUnresolved:
      'Özgün değer için eşleşen seçenek yok. Geçerli bir seçenek belirleyin veya değeri temizleyin.',
    nativeFieldEvidenceInvalid: 'Bu alanın kaynağı doğrulanamadı. Lütfen yöneticiye başvurun.',
    sourceReference: 'Kaynak referansı',
    showProvenance: 'Veri kaynağı',
    sourceField: 'Kaynak alan',
    sourceDefinition: 'Kaynak alan tanımı',
    sourceImageProcessingProtected:
      'İçe aktarılan özgün görüntünün türevleri kanıt olarak korunur. Gerekirse yeni yüklenen bir görüntü kullanın.',
    sourceImageReplaceProtected:
      'Geçişteki özgün görüntü kanıt olarak korunur. Yeni bir görüntü yükleyin ve gerekirse eski görüntüyü mevcut koleksiyondan çıkarın.',
    bulkDecisionRequired:
      'Genel toplu içe aktarma, alan bazında onaylı bir karar olmadan bu aktarılmış güncel kaydın üzerine yazamaz. Normal formunda düzenleyin veya doğrulanmış aktarımı tekrar çalıştırın.',
    receivablesDeferred: 'Açılış alacakları onaylı devir tarihini ve kaynak mutabakatını bekler.',
    sourcePreserved: 'Özgün kaynak verileri değişmeden korunur.',
    editableExplanation:
      'Güncel ana veriler normal yetkilerle düzenlenebilir. Eksik bilgiler, bu bilgilere bağlı işlemlerden önce kontrol edilir.',
    incompleteCurrentFacts: 'Bazı işlemler için güncel bilgiler henüz eksik.',
    priceRequired: 'Açıkça bir fiyat girin veya geçerli bir fiyat koşulu seçin.',
    productInactive: 'Bu ürün etkin değil veya arşivlenmiş; yeni bir işlem için seçilemez.',
    discountRestricted:
      'Kaynakta indirim yasağı var. İndirim uygulamadan önce bu kısıtlamanın kapsamını netleştirin.',
    conversionRequired:
      'Bu ambalaj dönüşümü henüz onaylanmadı. İncelenene kadar özgün birimi kullanın.',
    inventoryDeferred: 'Stok hareketleri, onaylı açılış sayımını ve devir tarihini bekler.',
    requiredFor: {
      EDIT: 'Bu işlemden önce belirtilen bilgileri tamamlayın.',
      DRAFT: 'Bu işlemden önce belirtilen bilgileri tamamlayın.',
      TAX: 'Bu işlemden önce belirtilen bilgileri tamamlayın.',
      PRICE: 'Bu işlemden önce belirtilen bilgileri tamamlayın.',
      STOCK: 'Bu işlemden önce belirtilen bilgileri tamamlayın.',
      BILLING: 'Bu işlemden önce belirtilen bilgileri tamamlayın.',
      SEND: 'Bu işlemden önce belirtilen bilgileri tamamlayın.',
      PORTAL: 'Bu işlemden önce belirtilen bilgileri tamamlayın.',
    },
    title: 'Veri kaynağı',
    masterReviewRequired: 'İçe aktarılan veriler iş onayı gerektirir.',
    holdExplanation:
      'Yeni işlemler, gönderimler ve stok hareketleri engellenir. Özgün değerler görüntülenebilir; eksik bilgiler varsayılanlarla doldurulmaz. Stok bakiyeleri onaylanmış bir açılış sayımını bekler.',
    origin: 'Kaynak · {{system}}',
    originalFields: 'Özgün ek alanlar',
    sourceFacts: 'Korunan kaynak değerleri',
    loading: 'Kaynak kanıtı yükleniyor',
    error: 'Kaynak kanıtı yüklenemedi.',
    noEvidence: 'İçe aktarma, doğrulanmış kaynak kanıtını henüz yayımlamadı.',
    noFields: 'Bu kaynak kaydında doldurulmuş ek alan yok.',
  },
};
